import React, { createContext, useState } from 'react';
import { IAppointmentConfig } from '@bb/domain/dist/types';
import { AgentType, ActionType } from 'modals/AppointmentActionModal/types';
import LayoutContext from 'contexts/LayoutContext';
import { getListingAppointmentConfig } from 'api/AppointmentConfigController';
import errorReporter from '@app/errorReporter';
import { getAppointment } from 'api/AppointmentController';
import message from '../../components/Message';
import {
  ActionModalAppointmentType,
  AppointmentActionModalContextType,
  OpenAppointmentActionModalOptions,
} from './types';
import { CoopCancelReasonType } from '../../app/DashboardComponents/CoopCancelAppointmentComponent';

const AppointmentActionModalContext =
  createContext<AppointmentActionModalContextType>(
    {} as AppointmentActionModalContextType,
  );

export const useAppointmentActionModalContext = () => {
  const context = React.useContext(AppointmentActionModalContext);
  if (context === undefined) {
    throw new Error(
      'useAppointmentActionModalContext must be used within a AppointmentActionModalContextProvider',
    );
  }
  return context;
};

export const AppointmentActionModalContextProvider: React.FC<{
  setChatVisibility?: (visible: boolean) => void;
  isMobileView?: boolean;
  isExternal?: boolean;
}> = ({ children, setChatVisibility, isMobileView, isExternal }) => {
  const [agent, setAgent] = useState<AgentType>();
  const [action, setAction] = useState<ActionType>();
  const [appointment, setAppointment] = useState<ActionModalAppointmentType>();
  const [appointmentConfig, setAppointmentConfig] =
    useState<IAppointmentConfig>();
  const [onClose, setOnClose] = useState<() => void>();
  const [cancelFromTour, setCancelFromTour] =
    useState<
      (
        reason: CoopCancelReasonType,
        details: string,
        onConfirm?: () => void,
      ) => Promise<void>
    >();
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  const [onConfirm, setOnConfirm] = useState<() => void>(() => {});

  const [loadingAppointmentConfig, setLoadingAppointmentConfig] =
    useState(false);
  const [loadingAppointment, setLoadingAppointment] = useState(false);
  const [visible, setVisible] = useState(false);

  const useLayoutContext = React.useContext(LayoutContext);

  // Angular pages don't have access to global contexts from the router,
  // so we must optionally pass in desired context functions for use in angular pages.
  const _setChatVisibility = !isExternal
    ? setChatVisibility || useLayoutContext.setChatVisibility
    : undefined;
  const _isMobileView = !isExternal
    ? (isMobileView ?? useLayoutContext.isMobileView)
    : false;

  const fetchAppointment = async (
    appointmentId: string,
    fetchConfig?: boolean,
  ) => {
    try {
      setLoadingAppointment(true);
      const { appointment: _appointment, listing } =
        await getAppointment(appointmentId);
      setAppointment({
        ..._appointment,
        listing: {
          ..._appointment.listing,
          thumbnail: listing.thumbnail,
        },
      });
      if (fetchConfig) fetchAppointmentConfig(listing.id);
    } catch (err) {
      errorReporter.error(`Error fetching appointment: ${err}`);
      closeModal();
      message.error('Error fetching appointment');
    } finally {
      setLoadingAppointment(false);
    }
  };

  const fetchAppointmentConfig = async (listingId: string) => {
    try {
      setLoadingAppointmentConfig(true);
      const _appointmentConfig = await getListingAppointmentConfig(listingId);
      setAppointmentConfig(_appointmentConfig);
    } catch (err) {
      errorReporter.error(`Error fetching appointment config: ${err}`);
      closeModal();
      message.error('Error fetching appointment details');
    } finally {
      setLoadingAppointmentConfig(false);
    }
  };

  const openModal = (opts: OpenAppointmentActionModalOptions) => {
    if (!opts.appointment && !opts.appointmentId) {
      const errorMessage = `Must provide either appointment or appointmentId to open Appointment Action Modal.`;
      errorReporter.error(errorMessage);
      throw new Error(errorMessage);
    }

    const fetchConfig = opts.agent === 'listing' && opts.action === 'confirm';

    // If we're only provided the appointmentId, must fetch appointment
    if (!opts.appointment) {
      fetchAppointment(opts.appointmentId, fetchConfig);
    } else {
      setAppointment(opts.appointment);
      if (fetchConfig) fetchAppointmentConfig(opts.appointment.listing.id);
    }

    setAgent(opts.agent);
    setAction(opts.action);
    setCancelFromTour(() => opts.cancelFromTour);
    setOnConfirm(() => () => {
      setCancelFromTour(undefined);
      if (opts.onConfirm) {
        opts.onConfirm();
      }
    });
    setOnClose(() => opts.onClose);

    setVisible(true);
    if (!isExternal) {
      _setChatVisibility(false);
    }
  };

  const closeModal = () => {
    setVisible(false);
    if (!isExternal) {
      _setChatVisibility(true);
    }
  };

  return (
    <AppointmentActionModalContext.Provider
      value={{
        visible,
        loadingAppointment,
        loadingAppointmentConfig,
        openModal,
        closeModal,
        onConfirm,
        onClose,
        cancelFromTour,
        agent,
        action,
        appointment,
        appointmentConfig,
        isMobileView: _isMobileView,
      }}
    >
      {children}
    </AppointmentActionModalContext.Provider>
  );
};

export default AppointmentActionModalContext;
